"use client";
import { Search, X } from "lucide-react";
import {
  Activity,
  ComponentProps,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import useLocalizer from "@/hooks/useLocalizer";

interface NavbarSearchInputProps extends ComponentProps<"input"> {
  containerClassName?: string;
}

const NavbarSearchInput: React.FC<NavbarSearchInputProps> = ({
  className,
  containerClassName,
  ...props
}) => {
  const { t } = useLocalizer();
  const inputRef = useRef<HTMLInputElement>(null);
  const [text, setText] = useState<string | undefined>(undefined);

  useEffect(() => {
    function handleInput() {
      setText(inputRef.current?.value);
    }

    if (inputRef.current) {
      inputRef.current.addEventListener("input", handleInput);
    }

    return () => {
      if (inputRef.current) {
        inputRef.current.removeEventListener("input", handleInput);
      }
    };
  }, [inputRef.current]);

  const handleClearText = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.value = "";
      setText(undefined);
      inputRef.current.focus();
    }
  }, [inputRef.current]);

  return (
    <div
      className={cn(
        "flex flex-row items-center justify-center bg-white h-20 rounded-sm px-4 w-full border border-border mt-40",
        containerClassName,
      )}
    >
      <Search size={24} className="text-secondary-foreground" />
      <Input
        tabIndex={1}
        ref={inputRef}
        className="border-none! shadow-none lg:text-lg text-sm ring-0! outline-0! focus:ring-0! bg-transparent!"
        {...props}
      />
      <Activity mode={text ? "visible" : "hidden"}>
        <Button
          variant="secondary"
          className="h-10 w-10 border border-secondary-foreground hover:[&>svg]:scale-120"
          onClick={handleClearText}
          aria-label={t("buttons.clear")}
        >
          <X size={24} />
        </Button>
      </Activity>
    </div>
  );
};

export const NavbarSearchButton: React.FC = () => {
  const { t } = useLocalizer();
  return (
    <Dialog modal>
      <DialogTrigger asChild>
        <button className="inline-flex items-center" aria-label="Search">
          <Search
            size={32}
            className="text-secondary-foreground hover:text-primary"
          />
        </button>
      </DialogTrigger>
      <DialogContent
        showCloseButton={false}
        className="bg-transparent! border-none! min-w-full top-[25%] shadow-none z-9999"
      >
        <DialogHeader>
          <DialogTitle />
        </DialogHeader>
        <NavbarSearchInput placeholder={t("placeholders.searchAnyThing")} />
      </DialogContent>
    </Dialog>
  );
};

export default NavbarSearchButton;
